import React, { useState } from "react";
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Collapse,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Link } from "react-router-dom";

const helpData = {
  LoadMoney: [
    {
      question: "How does my wallet get loaded?",
      answer: `Once wallet a/c is created after successful registration, money can be loaded into the wallet a/c through any of the following methods:

• Payment Gateway: Debit Card, Credit Card, Net Banking
• Online Banking: IMPS, NEFT
• Online Payment: UPI

Available on:
• Mobile/Web App for Cards/Net Banking
• Mobile App for UPI
• Bank Portal for IMPS/NEFT`,
    },
    {
      question:
        "What is the maximum & minimum amount that I can load into my wallet?",
      answer: `Please refer to "Load money limits" Section in "Terms & Conditions`,
    },
    {
      question:
        "Is there any restriction on number of load money transactions?",
      answer: `There is no restriction on the number of Load Money transactions.`,
    },
    {
      question:
        "I am unable to load money into my wallet account using my debit card/credit card/ net banking. What should I do?",
      answer: `Possible reasons:
• Issuer bank's network issue
• Poor internet connectivity
• Insufficient bank balance
• Bank account not activated for online payments

Try again later. If issue persists, contact: support@taipl.in`,
    },
    {
      question: "I was redirected to a page! What is payment gateway?",
      answer: `A Payment Gateway is an online payment service used by e-commerce portals. It provides internet payment options and is completely safe.`,
    },
    {
      question:
        "My money has been debited from bank account, but not credited in my wallet account. What should I do?",
      answer: `Possible reasons:
• Failed internet connection
• Delay at bank or payment gateway

Action:
Email support@taipl.in with transaction details and a screenshot (if possible). The issue will be resolved promptly.`,
    },
    {
      question:
        "Is there any charge for loading the money through Payment Gateway?",
      answer: (
        <>
          Please refer to{" "}
          <Link
            to="/termsandconditions?tab=limits#Load Money Charges"
            style={{ color: "#1976d2", textDecoration: "underline" }}
          >
            Load Money Charges
          </Link>{" "}
          Section in <Link to="/termsandconditions">Terms & Conditions</Link>.
        </>
      ),
    },
  ],
};

const Loadmoney = ({ selectedSection = "LoadMoney" }) => {
  const questions = helpData[selectedSection] || [];
  const [openIndex, setOpenIndex] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const toggleAnswer = (index) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  return (
    <Box width="100%">
      {/* Top Banner */}
      <Box
        sx={{
          width: "100%",
          height: { xs: 180, sm: 220, md: 260 },
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: { xs: 1, sm: 3 },
        }}
      >
        <Box
          component="img"
          src="/Assets/LoadMoney.jpg"
          alt="Profile illustration"
          sx={{
            width: "100%",
            maxWidth: 1000,
            height: "100%",
            objectFit: "cover",
            borderRadius: "0 0 10px 10px",
          }}
        />
      </Box>

      {/* FAQ Heading */}
      <Box mt={2} display="flex" justifyContent="center">
        <Typography
          variant={isMobile ? "h6" : "h5"}
          fontWeight="bold"
          color="black"
        >
          Load Money
        </Typography>
      </Box>

      {/* FAQ List */}
      <Box
        sx={{
          width: "100%",
          maxWidth: "900px",
          mx: "auto",
          px: { xs: 1.5, sm: 2, md: 3 },
          mt: 2,
          py: 2,
        }}
      >
        <List disablePadding>
          {questions.map((item, index) => (
            <Box
              key={index}
              sx={{
                border: "1px solid #ddd",
                borderRadius: 2,
                mb: 2,
                overflow: "hidden",
                boxShadow:
                  openIndex === index ? "0 2px 8px rgba(0,0,0,0.06)" : "none",
                transition: "box-shadow 0.2s ease-in-out",
              }}
            >
              <ListItemButton
                onClick={() => toggleAnswer(index)}
                sx={{
                  borderBottom: "1px solid #eee",
                  px: 2,
                  py: 1.5,
                  borderRadius: 1,
                  backgroundColor:
                    openIndex === index ? "#e0f7fa" : "transparent",
                  "&:hover": {
                    backgroundColor:
                      openIndex === index ? "#b2ebf2" : "#f9f9f9",
                  },
                }}
              >
                <ListItemText primary={item.question} />
                {openIndex === index ? (
                  <ExpandMoreIcon sx={{ fontSize: 18 }} />
                ) : (
                  <ArrowForwardIosIcon sx={{ fontSize: 14 }} />
                )}
              </ListItemButton>

              <Collapse in={openIndex === index} timeout="auto" unmountOnExit>
                <Box px={3} py={2} bgcolor="#fafafa">
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}
                  >
                    {item.answer}
                  </Typography>
                </Box>
              </Collapse>
            </Box>
          ))}
        </List>
      </Box>
    </Box>
  );
};

export default Loadmoney;
